import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { Role } from '../../../types/role.types';
import { User, UserWizardData } from '../../../types/user.types';
import { useUserWizard } from './UserWizard.hooks';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { CloseConfirmationModal } from '../../modals/confirmation';
import ProgressBar from '../../inventory/add-panel/ProgressBar';
import StepIndicators, { StepConfig } from '../../inventory/add-panel/StepIndicators';
import WizardStep1 from './WizardStep1';
import WizardStep2 from './WizardStep2';
import WizardStep3 from './WizardStep3';
import { LocationsService, RolesService } from '../../../api';
import { mapApiRoleToRole } from '../../../types/role.types';
import type { ApiRole } from '../../../types/role.types';
import type { Location } from '../../../api';
import { USE_MOCK_USERS } from '../../../config/feature-flags';
import { MOCK_ROLES } from '../../../utils/mockUsers';
import {
  StyledDrawer,
  DrawerContainer,
  DrawerHeader,
  DrawerTitle,
  CloseButton,
  DrawerContent,
  DrawerFooter,
  CancelButton,
  NextButton,
  BackButton,
  SaveButton,
} from './UserWizard.styles';

export interface UserWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (userData: UserWizardData) => Promise<void>;
  editUser?: User | null;
  roles?: Role[];
  /** Active branch locations; when provided, skips an extra locations fetch. */
  locations?: Location[];
}

const userSteps: StepConfig[] = [
  {
    id: 1,
    title: 'Información Básica',
    icon: <PersonIcon fontSize="small" />,
    color: {
      active: 'bg-sky-600',
      completed: 'bg-sky-100',
      inactive: 'bg-gray-200',
    },
    textColor: {
      active: 'text-white',
      completed: 'text-sky-800',
      inactive: 'text-gray-500',
    },
  },
  {
    id: 2,
    title: 'Roles',
    icon: <SecurityIcon fontSize="small" />,
    color: {
      active: 'bg-emerald-600',
      completed: 'bg-emerald-100',
      inactive: 'bg-gray-200',
    },
    textColor: {
      active: 'text-white',
      completed: 'text-emerald-800',
      inactive: 'text-gray-500',
    },
  },
  {
    id: 3,
    title: 'Confirmación',
    icon: <CheckCircleIcon fontSize="small" />,
    color: {
      active: 'bg-indigo-600',
      completed: 'bg-indigo-100',
      inactive: 'bg-gray-200',
    },
    textColor: {
      active: 'text-white',
      completed: 'text-indigo-800',
      inactive: 'text-gray-500',
    },
  },
];

export const UserWizard: React.FC<UserWizardProps> = ({
  isOpen,
  onClose,
  onSave,
  editUser,
  roles = [],
  locations = [],
}) => {
  const wizard = useUserWizard({
    initialUser: editUser || null,
    onSave,
    onClose,
    isOpen,
  });

  const [availableRoles, setAvailableRoles] = useState<Role[]>(roles);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [availableLocations, setAvailableLocations] = useState<Location[]>(locations);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Hook para cerrar con ESC - callback primero, luego isEnabled
  useEscapeKey(wizard.handleCloseClick, isOpen);

  useEffect(() => {
    if (roles.length > 0) {
      setAvailableRoles(roles);
    }
  }, [roles]);

  useEffect(() => {
    // If we have roles from props, use them
    if (roles.length > 0) {
      return;
    }

    // Only fetch if drawer is open
    if (!isOpen) {
      return;
    }

    if (USE_MOCK_USERS) {
      setAvailableRoles(MOCK_ROLES);
      return;
    }

    const fetchRoles = async () => {
      setLoadingRoles(true);
      try {
        const response = await RolesService.rolesControllerFindAll(
          1,
          100, // Backend limit is 100
          undefined,
          undefined,
          undefined,
          undefined,
          'name',
        );
        const rolesData = (response?.data as ApiRole[])?.map((role) => mapApiRoleToRole(role));
        setAvailableRoles(rolesData || []);
      } catch (error) {
        console.error('UserWizard: Error fetching roles:', error);
        setAvailableRoles([]);
      } finally {
        setLoadingRoles(false);
      }
    };

    fetchRoles();
  }, [roles.length, isOpen]);

  useEffect(() => {
    if (locations.length > 0) {
      setAvailableLocations(locations);
      return;
    }

    if (!isOpen) return;

    if (USE_MOCK_USERS) {
      setAvailableLocations([]);
      return;
    }

    const fetchLocations = async () => {
      setLoadingLocations(true);
      try {
        const response = await LocationsService.locationsControllerFindAll();
        const locationsData = Array.isArray(response?.data) ? (response.data as Location[]) : [];
        const branchLocations = locationsData.filter(
          (location) => location.type === 'BRANCH' && location.status === 'ACTIVE',
        );
        setAvailableLocations(branchLocations);
      } catch (error) {
        console.error('UserWizard: Error fetching locations:', error);
        setAvailableLocations([]);
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchLocations();
  }, [isOpen, locations]);

  const handleToggleRole = (roleId: string) => {
    const roleIds = wizard.formData.roleIds.includes(roleId)
      ? wizard.formData.roleIds.filter((id) => id !== roleId)
      : [...wizard.formData.roleIds, roleId];
    wizard.updateField('roleIds', roleIds);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await wizard.handleSave();
    } finally {
      setIsSaving(false);
    }
  };

  const stepContent = useMemo(() => {
    if (wizard.currentStep === 1) {
      return (
        <WizardStep1
          formData={wizard.formData}
          onChange={(name, value) => wizard.updateField(name, value)}
          errors={wizard.errors}
          isEditMode={wizard.isEditMode}
          locations={availableLocations}
          loadingLocations={loadingLocations}
        />
      );
    }

    if (wizard.currentStep === 2) {
      return (
        <WizardStep2
          roleIds={wizard.formData.roleIds}
          roles={availableRoles}
          onToggleRole={handleToggleRole}
          error={wizard.errors.roleIds}
        />
      );
    }

    return (
      <WizardStep3
        formData={wizard.formData}
        roles={availableRoles}
        locations={availableLocations}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizard, availableRoles, availableLocations, loadingLocations]);

  return (
    <>
      <StyledDrawer anchor="right" open={isOpen} onClose={wizard.handleCloseClick}>
        <DrawerContainer>
          <DrawerHeader>
            <DrawerTitle>{editUser ? 'Editar Usuario' : 'Nuevo Usuario'}</DrawerTitle>
            <CloseButton onClick={wizard.handleCloseClick}>
              <CloseIcon />
            </CloseButton>
          </DrawerHeader>
          <ProgressBar currentStep={wizard.currentStep} totalSteps={3} />
          <StepIndicators steps={userSteps} currentStep={wizard.currentStep} />
          <DrawerContent>
            {loadingRoles ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              stepContent
            )}
          </DrawerContent>
          <DrawerFooter>
            <CancelButton variant="outlined" onClick={wizard.handleCloseClick}>
              Cancelar
            </CancelButton>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {wizard.canGoPrev && (
                <BackButton variant="outlined" onClick={wizard.prevStep}>
                  Atrás
                </BackButton>
              )}
              {wizard.currentStep < 3 && (
                <NextButton
                  variant="contained"
                  onClick={wizard.nextStep}
                  disabled={!wizard.canGoNext}
                >
                  Siguiente
                </NextButton>
              )}
              {wizard.currentStep === 3 && (
                <SaveButton variant="contained" onClick={handleSave} disabled={isSaving}>
                  {isSaving ? 'Guardando...' : 'Guardar'}
                </SaveButton>
              )}
            </Box>
          </DrawerFooter>
        </DrawerContainer>
      </StyledDrawer>

      <CloseConfirmationModal
        open={wizard.showCancelConfirmation}
        onClose={() => wizard.setShowCancelConfirmation(false)}
        onConfirm={wizard.cancelOperation}
      />
    </>
  );
};

export default UserWizard;
